import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const AuthContext = createContext(null);

const API = '/api';

async function apiFetch(path, options = {}) {
  const token = sessionStorage.getItem('token');
  const headers = {
    ...(options.isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  const { isFormData, ...fetchOptions } = options;
  const res = await fetch(`${API}${path}`, {
    ...fetchOptions,
    headers,
  });
  if (res.status === 401) {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }
  return res;
}

const WRITE_GRANTING_GROUPS = [
  'Super Admins', 'Engagement Admins', 'Client Admins', 'Technical Managers',
  'MSP Billing Admins', 'Client Billing Admins', 'Report Admins', 'Penetration Testers', 'MSP Penetration Testers',
];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('user')); } catch { return null; }
  });
  const [token, setToken] = useState(() => sessionStorage.getItem('token'));

  // Block routing until sso_token validation completes (prevents redirect-before-check race)
  const [authLoading, setAuthLoading] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return Boolean(params.get('sso_token'));
  });

  // SSO token pickup from URL (used by "Launch Local Client" button on SaaS)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('sso_token');
    if (!ssoToken) return;
    const cleanUrl = () => {
      const url = new URL(window.location);
      url.searchParams.delete('sso_token');
      window.history.replaceState({}, '', url.pathname + (url.search || ''));
    };
    fetch(`${API}/auth/me`, {
      headers: { 'Authorization': `Bearer ${ssoToken}` },
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(userData => {
        sessionStorage.setItem('token', ssoToken);
        sessionStorage.setItem('user', JSON.stringify(userData));
        setToken(ssoToken);
        setUser(userData);
      })
      .catch(() => {}) // Token invalid — fall through to normal login
      .finally(() => { cleanUrl(); setAuthLoading(false); });
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    sessionStorage.setItem('token', data.token);
    sessionStorage.setItem('user', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const loginWithToken = useCallback((token, userData) => {
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('user', JSON.stringify(userData));
    setToken(token);
    setUser(userData);
  }, []);

  const logout = useCallback(async () => {
    try { await apiFetch('/auth/logout', { method: 'POST' }); } catch {}
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    setToken(null);
    setUser(null);
  }, []);

  const hasGroup = useCallback((group) => {
    if (!user) return false;
    if (user.groups?.includes('Super Admins')) return true;
    return user.groups?.includes(group);
  }, [user]);

  const isMsp = user?.isMsp || user?.groups?.includes('Super Admins');

  // Mirrors enforceReadOnlyViewer in backend/middleware/auth.js. Group grants
  // are additive, so Client Viewers only means read-only when it is the
  // account's sole source of access. The API is the boundary — this just keeps
  // the interface from offering actions that would be refused.
  // Kept in step with WRITE_GRANTING_GROUPS in backend/middleware/auth.js.
  const WRITE_GRANTING_GROUPS = [
    'Super Admins', 'Engagement Admins', 'Client Admins', 'Technical Managers',
    'MSP Billing Admins', 'Client Billing Admins', 'Report Admins',
    'Penetration Testers', 'MSP Penetration Testers', 'Application Managers',
  ];

  const isReadOnlyViewer = !!user?.groups?.includes('Client Viewers') &&
    !user?.groups?.some(g => WRITE_GRANTING_GROUPS.includes(g));

  // Requestors held on its own is the account's entire access: the Requests
  // page and nothing else. Mirrors confiningGroups in backend/middleware/auth.js,
  // which is what actually enforces it — this only decides what to show.
  const isRequestorOnly = !!user?.groups?.includes('Requestors') &&
    !user?.groups?.some(g => WRITE_GRANTING_GROUPS.includes(g));

  return (
    <AuthContext.Provider value={{ user, token, authLoading, login, loginWithToken, logout, hasGroup, isMsp, isReadOnlyViewer, isRequestorOnly, apiFetch }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { apiFetch };
